import type { AgencyApplicationField } from '~/lib/agency-application-validation';
import { cardsTrustedBySection } from './cards';
import { getAppCta } from './navigation';
import type {
  FeatureBlockContent,
  HeroContent,
  IconName,
  ImageRef,
  LinkItem,
  TestimonialGridContent,
  TextSectionContent,
} from './types';

/* Marketer landing page copy (Figma "Marketer Landing Page", 1272:49002 / mobile 1101:18017). */

/** In-page anchor of the agency application form, targeted by every "Apply for Infinite" CTA. */
export const MARKETERS_APPLY_ID = 'apply';

export const marketersHero: HeroContent = {
  title: 'The nsave card for marketing spend',
  cta: { label: 'Get your nsave card', href: getAppCta.href },
  image: {
    src: '/images/marketers/hero.jpg',
    webpSrc: '/images/marketers/hero.webp',
    mobileSrc: '/images/marketers/hero-mobile.jpg',
    mobileWebpSrc: '/images/marketers/hero-mobile.webp',
    alt: 'Marketer reviewing campaign analytics on a laptop',
  },
};

export const marketersTrustedBySection: TextSectionContent = {
  ...cardsTrustedBySection,
  mobileBody:
    'nsave opens up the global banking system to people the traditional one leaves behind. Wherever you are, you can hold USD, GBP and EUR, get real account details in your name, and move money across borders the way people in stable economies always have.',
};

export const marketersOfferBanner = {
  text: 'Running your first Google or Meta ad? Get 10% cashback, up to $100. Limited offer until 30 November, 2026.',
  learnMore: 'Learn more',
};

export const marketersOfferModal: {
  title: string;
  body: string;
  howItWorksTitle: string;
  steps: Array<{ icon: IconName; text: string; mobileText?: string }>;
  noCardTitle: string;
  noCardBody: string;
  cta: string;
  terms: string;
} = {
  title: "Marketers, it's time your ad spend paid you back.",
  body: 'Launching your first Google or Meta ad campaign? Make your first ad payment with your nsave card and get 10% cashback, up to $100. Limited offer until 30 November, 2026.',
  howItWorksTitle: 'How it works',
  steps: [
    { icon: 'card', text: 'Get your nsave card ready' },
    {
      icon: 'chart',
      text: 'Run your first ad with either Google or Meta ads',
      mobileText: 'Pay for either Google or Meta ads',
    },
    { icon: 'dollar', text: 'Earn 10% cashback' },
  ],
  noCardTitle: 'Don’t have an nsave card?',
  noCardBody: 'Claim your free infinite gold card and start making your ad spend work harder.',
  cta: 'Apply for Infinite',
  terms: 'Terms and Conditions apply',
};

export interface UsdAccountCard {
  id: 'get-paid' | 'skip-fx' | 'invest';
  title: string;
  description: string;
}

export const marketersUsdAccount: { title: string; cards: UsdAccountCard[] } = {
  title: 'Your USD Account',
  cards: [
    {
      id: 'get-paid',
      title: 'Get paid in USD',
      description: 'Receive payments from international clients directly into your USD account.',
    },
    {
      id: 'skip-fx',
      title: 'Skip unnecessary FX',
      description: 'Get paid in USD and keep your earnings in USD without unnecessary conversions.',
    },
    {
      id: 'invest',
      title: 'Invest your USD',
      description: 'Hold your earnings in USD, choose to invest, or earn rewards on your balance',
    },
  ],
};

export const marketersPaymentReceived = {
  label: 'Payment Received',
  amount: '+2,500.00 $',
};

export const marketersAccountDetailsImage: ImageRef = {
  src: '/images/marketers/account-details.jpg',
  webpSrc: '/images/marketers/account-details.webp',
  alt: 'USD account details in the nsave app',
};

export const marketersInvestMonths = ['Mar', 'Apr', 'May', 'Jun'];

/** No image: the page renders the coded transactions panel in its place. */
export const marketersCard: Omit<FeatureBlockContent, 'image'> = {
  title: 'The nsave Card',
  rows: [
    {
      icon: 'trend',
      title: 'Run your ads',
      description: 'Pay for platforms you use to reach customers and grow your business.',
    },
    {
      icon: 'copy',
      title: 'Cover your business tools',
      description: 'Pay for AI tools, software and subscriptions with your nsave card.',
    },
    {
      icon: 'card',
      title: 'Pay directly in USD',
      description:
        'Pay ad platforms in USD with the nsave card, powered by digital assets and accepted wherever Visa is accepted.',
    },
  ],
};

/** Logo sizes are design pixels on the 51.9px avatar. */
export const marketersCardTransactions: Array<{
  merchant: string;
  amount: string;
  logo: string;
  logoWidth: number;
  logoHeight: number;
}> = [
  {
    merchant: 'Meta Ads',
    amount: '- 450.00 $',
    logo: '/images/marketers/meta.png',
    logoWidth: 37.29,
    logoHeight: 24.77,
  },
  {
    merchant: 'Google Ads',
    amount: '- 210.00 $',
    logo: '/images/marketers/google.png',
    logoWidth: 33.14,
    logoHeight: 33.14,
  },
  {
    merchant: 'Open AI',
    amount: '- 20.00 $',
    logo: '/images/marketers/openai.png',
    logoWidth: 35.51,
    logoHeight: 35.51,
  },
  {
    merchant: 'Figma',
    amount: '- 90.00 $',
    logo: '/images/marketers/figma.png',
    logoWidth: 23.67,
    logoHeight: 35.51,
  },
];

export const marketersCardEveryday: FeatureBlockContent = {
  title: 'Your Card, Everyday',
  image: {
    src: '/images/marketers/card-everyday.jpg',
    webpSrc: '/images/marketers/card-everyday.webp',
    alt: 'Hands holding a phone showing the red nsave card in the app',
  },
  rows: [
    {
      icon: 'earth',
      title: 'Pay wherever business takes you',
      description: 'Use your nsave card online and at Visa merchants worldwide.',
    },
    {
      icon: 'phone-vibrate',
      title: 'Apple Pay and Google Pay',
      description: 'Add the card to your wallet and pay across your marketing stack in seconds.',
    },
  ],
};

export const marketersAgency: {
  title: string;
  body: string;
  image: ImageRef;
  fields: Array<{
    name: AgencyApplicationField;
    placeholder: string;
    type: 'text' | 'email' | 'tel';
    autoComplete: string;
  }>;
  cta: LinkItem['label'];
  successTitle: string;
  successBody: string;
} = {
  title: 'Are you an agency owner?',
  body: 'We are offering a free infinite card to agency owners to post their first campaign. Tell us about the spend you manage and we’ll review your application.',
  image: {
    src: '/images/marketers/agency.jpg',
    webpSrc: '/images/marketers/agency.webp',
    mobileSrc: '/images/marketers/agency-mobile.jpg',
    mobileWebpSrc: '/images/marketers/agency-mobile.webp',
    alt: 'Gold nsave Visa Infinite card standing in desert sand',
  },
  fields: [
    {
      name: 'agencyName',
      placeholder: 'Name of agency',
      type: 'text',
      autoComplete: 'organization',
    },
    {
      name: 'monthlySpend',
      placeholder: 'Expected monthly spend',
      type: 'text',
      autoComplete: 'off',
    },
    { name: 'country', placeholder: 'Country', type: 'text', autoComplete: 'country-name' },
    { name: 'email', placeholder: 'Email', type: 'email', autoComplete: 'email' },
    {
      name: 'phone',
      placeholder: 'Phone number (+ country code)',
      type: 'tel',
      autoComplete: 'tel',
    },
  ],
  cta: 'Apply for Infinite',
  successTitle: 'Application received.',
  successBody: 'Thanks for applying. We’ll review your application and email you.',
};

export const marketersCardDisclaimer =
  '*The nsave Card is issued by Rain, a Visa Principal Member, under license from Visa, and accepted wherever Visa is accepted. It is separate from nsave’s other services, including USD accounts, investing, balance rewards, and transfers, which are governed by their own terms. Rain is not a party to agreements concerning these services and is not responsible for non-card transactions, loans, or arrangements.';

export const marketersTestimonials: TestimonialGridContent = {
  title: 'Built for people just like you',
  items: [
    {
      label: 'Marketers',
      image: {
        src: '/images/marketers/marketers.jpg',
        webpSrc: '/images/marketers/marketers.webp',
        alt: 'Marketer smiling while working at a desk',
      },
    },
    {
      label: 'Agencies',
      image: {
        src: '/images/marketers/agencies.jpg',
        webpSrc: '/images/marketers/agencies.webp',
        alt: 'Agency team working together in a bright office',
      },
    },
    {
      label: 'Freelancers',
      mobileLabel: 'Freelancer',
      image: {
        src: '/images/marketers/freelancers.jpg',
        webpSrc: '/images/marketers/freelancers.webp',
        alt: 'Freelancer checking a phone',
      },
    },
  ],
};
